/**
 * MOCK: the server's vCard import (backend `dav.ParseImport`, #241), for
 * `VITE_USE_MOCKS=true` and tests.
 *
 * It keeps the server's rules for the cases the UI branches on:
 * - a card with no END, or a line that is not a property, fails `malformed`;
 * - a card with no name, organisation or email fails `no_name`;
 * - a card with no UID gets one;
 * - a UID already in the book, or earlier in the file, is skipped.
 *
 * It does not reproduce the 2.1 upgrade or the photo rule. Those are the
 * backend's tests' business; the mock only has to make the UI's states
 * reachable.
 */

/** The file-level caps, as `contacts_import.go` has them. */
export const MOCK_IMPORT_MAX_BYTES = 5 * 1024 * 1024;
export const MOCK_IMPORT_MAX_CARDS = 2000;

export interface MockImportCard {
  index: number;
  uid: string;
  uidGenerated: boolean;
  displayName: string;
  firstName?: string;
  lastName?: string;
  organization?: string;
  emails: string[];
  phones: string[];
  error?: { reason: string; message: string };
}

/** Splits a .vcf file into cards; `[]` when it holds none at all. */
export function parseMockVcardFile(text: string, newUid: () => string): MockImportCard[] {
  const lines = text.replace(/^\uFEFF/, '').split(/\r\n|\r|\n/);
  const spans: { lines: string[]; error?: string }[] = [];
  let current: string[] | undefined;
  for (const line of lines) {
    const folded = line.startsWith(' ') || line.startsWith('\t');
    const keyword = folded ? '' : line.trim().toUpperCase();
    if (keyword === 'BEGIN:VCARD') {
      if (current) {
        spans.push({ lines: current, error: 'this card has no END:VCARD before the next card begins' });
      }
      current = [];
      continue;
    }
    if (keyword === 'END:VCARD') {
      if (current) spans.push({ lines: current });
      current = undefined;
      continue;
    }
    if (current === undefined) continue;
    if (folded && current.length > 0) current[current.length - 1] += line.slice(1);
    else current.push(line);
  }
  if (current) spans.push({ lines: current, error: "the file ends before this card's END:VCARD" });

  return spans.map((span, i) => toCard(i + 1, span.lines, span.error, newUid));
}

function toCard(
  index: number,
  lines: string[],
  spanError: string | undefined,
  newUid: () => string,
): MockImportCard {
  const props = new Map<string, string[]>();
  let malformed = spanError;
  for (const line of lines) {
    if (line.trim() === '') continue;
    const match = /^(?:[A-Za-z0-9-]+\.)?([A-Za-z0-9-]+)[;:]/.exec(line);
    const colon = line.indexOf(':');
    if (match === null || colon < 0) {
      malformed ??= 'this card has a line that is not a vCard property';
      continue;
    }
    const name = (match[1] ?? '').toUpperCase();
    props.set(name, [...(props.get(name) ?? []), line.slice(colon + 1).trim()]);
  }
  const first = (name: string) => props.get(name)?.[0] ?? '';
  const [lastName = '', firstName = ''] = first('N').split(';');
  const organization = first('ORG').split(';')[0] ?? '';
  const emails = props.get('EMAIL') ?? [];
  const displayName =
    first('FN') || `${firstName} ${lastName}`.trim() || organization || (emails[0] ?? '');
  const uid = first('UID');
  const card: MockImportCard = {
    index,
    uid: uid || newUid(),
    uidGenerated: uid === '',
    displayName,
    emails,
    phones: props.get('TEL') ?? [],
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
    ...(organization ? { organization } : {}),
  };
  if (malformed !== undefined) return { ...card, error: { reason: 'malformed', message: malformed } };
  if (displayName === '') {
    return {
      ...card,
      error: { reason: 'no_name', message: 'this card has no name, organisation or email address' },
    };
  }
  return card;
}
